import { Link } from 'react-router-dom';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import { useApi } from '../../lib/useApi.js';
import { useIsMobileShell } from '../../lib/useIsMobileShell.js';
import {
  LoadingSkeleton,
  EmptyState,
  AlertCard,
  KpiHeroBand,
  GaugeCard,
  InvestmentDonut,
  MissionImpactScatter,
  StatusDonutMini,
  KpiBandSkeleton,
  ChartSkeleton,
  BrandedEmptyState,
} from '../../components/index.js';
import { ErrorZone } from '../../components/primitives/ErrorZone.jsx';
import { StartHereStrip } from '../onboarding/StartHereStrip.jsx';
import { personaKeyFromRoles, primaryRoleLabel } from '../../lib/personaKey.js';
import './home.redesign.css';

const PERSONA_QUICKS = {
  'Executive Sponsor': [
    {
      to: '/executive',
      label: 'Open Command Center',
      desc: 'Mission pulse, predictive alerts, AI briefings.',
    },
    {
      to: '/projects/PRJ-9924-A/gate-review/GATE-SG-03',
      label: 'Sentinel Guard Gate 3',
      desc: 'Approve, defer, or revise the gate.',
    },
    {
      to: '/analytics/mission-impact',
      label: 'Mission impact',
      desc: 'ROI and outcome by portfolio.',
    },
    { to: '/exports', label: 'Briefings', desc: 'Approve and export briefings.' },
  ],
  'Portfolio Manager': [
    {
      to: '/delivery?projectId=INT-4092',
      label: 'AI Diagnostic delivery',
      desc: 'Sprint board with active blockers.',
    },
    { to: '/roadmap?focus=INT-4092', label: 'Roadmap slip view', desc: 'See the IOC drift.' },
    { to: '/ai/orchestration', label: 'AI orchestration', desc: 'Risk Sentinel run telemetry.' },
    {
      to: '/decision-support',
      label: 'Decision support',
      desc: 'Compare COAs and capture rationale.',
    },
  ],
  'Data Steward': [
    { to: '/admin/data', label: 'Connectors', desc: 'Sync, retry, audit feed.' },
    { to: '/admin/lineage', label: 'Lineage explorer', desc: 'Reconcile source → canonical.' },
    { to: '/admin/audit', label: 'Audit log', desc: 'Filter & deep-link entity history.' },
  ],
  default: [
    { to: '/access/status', label: 'Access status', desc: 'Check the current approval path.' },
    { to: '/access/request', label: 'Request access', desc: 'Start a new eSAAR request.' },
    { to: '/notifications', label: 'Notifications', desc: 'Review launch updates and follow-ups.' },
    { to: '/me', label: 'My profile', desc: 'Review your current persona and contact info.' },
  ],
};

const EMPTY_WORKSPACE_QUICKS = {
  'Executive Sponsor': [
    {
      to: '/executive',
      label: 'Open Command Center',
      desc: 'Review the clean launch workspace and current signals.',
    },
    {
      to: '/ai/ask',
      label: 'Ask PRIME',
      desc: 'Use grounded Q&A without prior business history.',
    },
    {
      to: '/access/status',
      label: 'Access status',
      desc: 'Review pending approvals and role readiness.',
    },
    {
      to: '/notifications',
      label: 'Notifications',
      desc: 'Check launch-time notices and next actions.',
    },
  ],
  'Portfolio Manager': [
    {
      to: '/portfolios',
      label: 'Portfolios',
      desc: 'Open the clean portfolio workspace and its empty states.',
    },
    {
      to: '/delivery',
      label: 'Delivery workspace',
      desc: 'Review the launch-state delivery shell.',
    },
    {
      to: '/access/request',
      label: 'Request access',
      desc: 'Submit a scoped access request.',
    },
    {
      to: '/notifications',
      label: 'Notifications',
      desc: 'Review updates for this persona.',
    },
  ],
  'Data Steward': [
    {
      to: '/admin/data',
      label: 'Connectors',
      desc: 'Inspect connector status without line-of-business data.',
    },
    {
      to: '/admin/lineage',
      label: 'Lineage explorer',
      desc: 'Review the clean lineage workspace.',
    },
    {
      to: '/admin/access-requests',
      label: 'Access reviews',
      desc: 'Work the initial approval queue.',
    },
    {
      to: '/admin/audit',
      label: 'Audit log',
      desc: 'Verify state changes in the launch workspace.',
    },
  ],
  default: PERSONA_QUICKS.default,
};

// Role lens per 05 §4: the lens decides which Mission Brief zones render.
// exec/portfolio keep the full portfolio-pulse visuals; delivery, steward,
// and contributor/pending lenses lead with their attention queues instead.
function deriveLens(title = '') {
  if (/executive/i.test(title)) return 'exec';
  if (/portfolio|program/i.test(title)) return 'portfolio';
  if (/project manager|delivery|scrum/i.test(title)) return 'delivery';
  if (/steward|system admin/i.test(title)) return 'steward';
  return 'contributor';
}

// FS-HOME-1 attention strip: per-persona queues assembled server-side by
// GET /home/attention. Counts are server totals for the owning queues
// (AC-HOME-5); every row deep-links to the owning surface (AC-HOME-1).
function AttentionStrip({ attention, quicks }) {
  if (attention.loading) {
    return (
      <div className="card home-attention" data-testid="home-attention-strip">
        <LoadingSkeleton rows={3} />
      </div>
    );
  }
  if (attention.error) {
    /* AC-STATE-1: an attention-queue failure banners only this zone; the
       rest of the Mission Brief keeps rendering. */
    return (
      <div className="home-attention" data-testid="home-attention-strip">
        <ErrorZone title="attention queue" error={attention.error} onRetry={attention.refresh} />
      </div>
    );
  }

  const payload = attention.data?.data || {};
  const queues = (payload.queues || []).filter((queue) => Number(queue.count || 0) > 0);
  const totalCount = Number(payload.totalCount || 0);
  const updatedChip = attention.loadedAt ? (
    <span className="chip attention-updated">
      Updated {attention.loadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ·
      live queues
    </span>
  ) : null;

  if (!queues.length || totalCount === 0) {
    /* AC-HOME-4: a genuine "You're clear" state with next-best links — no
       fabricated urgency. */
    return (
      <div className="card home-attention is-clear" data-testid="home-attention-strip">
        <header className="row spread attention-head">
          <div className="eyebrow attention-label sage">Needs your attention</div>
          {updatedChip}
        </header>
        <p className="attention-clear-copy">
          <strong>You&rsquo;re clear.</strong> No reviews, decisions, or overdue items are waiting
          on you.
          {quicks.length ? (
            <>
              {' '}
              Next useful stops:{' '}
              {quicks.slice(0, 2).map((q, index) => (
                <span key={q.to}>
                  {index > 0 ? ' or ' : ''}
                  <Link to={q.to}>{q.label}</Link>
                </span>
              ))}
              .
            </>
          ) : null}
        </p>
      </div>
    );
  }

  return (
    <div className="card home-attention" data-testid="home-attention-strip">
      <header className="row spread attention-head">
        <div className="eyebrow attention-label">
          Needs your attention · {totalCount} item{totalCount === 1 ? '' : 's'}
        </div>
        {updatedChip}
      </header>
      <ul className="attention-list">
        {queues.map((queue) => (
          <li
            className="attention-row"
            key={queue.key}
            data-testid={`attention-queue-${queue.key}`}
          >
            <span className={`attention-sev sev-${queue.severity || 'blue'}`} aria-hidden="true" />
            <div className="attention-txt">
              <div className="attention-title">
                <strong>{queue.label}</strong>
                <span
                  className="attention-count"
                  data-testid={`attention-count-${queue.key}`}
                  aria-label={`${queue.count} items`}
                >
                  {queue.count}
                </span>
              </div>
              {(queue.items || []).length ? (
                <small className="attention-items muted">
                  {(queue.items || []).map((item, index) => (
                    <span key={item.id || index}>
                      {index > 0 ? ' · ' : ''}
                      {item.route ? <Link to={item.route}>{item.title}</Link> : item.title}
                      {item.meta ? <span className="muted"> ({item.meta})</span> : null}
                    </span>
                  ))}
                </small>
              ) : null}
            </div>
            <Link className="btn ghost attention-go" to={queue.route}>
              {queue.cta || 'Open'} →
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Phone treatment for the mission-impact scatter (locked chart decision 2). A
// scatter plot is not legible at phone width, so on mobile we render a
// key-value summary of the SAME points (name → mission impact · planned spend,
// the chart's own data, no new analysis) and tuck the real scatter behind a
// "View chart" disclosure that renders it inside a contained .m-scroll frame.
// On desktop the scatter renders inline exactly as before.
function ScatterFitOrSummary({ points, isMobile }) {
  if (!isMobile) {
    return <MissionImpactScatter data={points} height={300} />;
  }
  const top = [...points].sort((a, b) => Number(b.impact || 0) - Number(a.impact || 0)).slice(0, 6);
  return (
    <div data-testid="home-scatter-mobile">
      <ul className="home-scatter-summary" data-testid="home-scatter-summary">
        {top.map((p) => (
          <li key={p.id || p.name}>
            <span className="home-scatter-name">{p.name}</span>
            <span className="home-scatter-metric">
              impact {Math.round(Number(p.impact || 0))} · ${Number(p.spend || 0).toFixed(0)}M
            </span>
          </li>
        ))}
      </ul>
      <details className="home-chart-disclosure">
        <summary data-testid="home-scatter-disclosure">View chart</summary>
        <div className="m-scroll home-scatter-scroll">
          <div className="home-scatter-scroll__inner">
            <MissionImpactScatter data={points} height={280} />
          </div>
        </div>
      </details>
    </div>
  );
}

export function NexusHomePage() {
  const { user, roles = [] } = useCurrentUser();
  const isMobileShell = useIsMobileShell();
  // Roles are the governed truth for who this account is; the title string is
  // only a fallback for legacy rows without role assignments (DEF-002 — a
  // managed Program Manager account must not land on the pending-access
  // "default" brief).
  const personaBucket = roles.length ? personaKeyFromRoles(roles) : null;
  const lens = personaBucket
    ? {
        executive_sponsor: 'exec',
        delivery_leader: 'portfolio',
        data_steward: 'steward',
        system_admin: 'steward',
        pending_access: 'contributor',
      }[personaBucket]
    : deriveLens(user?.title || '');
  // Portfolio-pulse visuals are the exec/portfolio lens zone; other lenses
  // lead with their queues, so the executive summary fetch is skipped.
  const showPulse = lens === 'exec' || lens === 'portfolio';
  const summary = useApi('/analytics/executive-summary', { skip: !showPulse });
  const attention = useApi('/home/attention');
  const notifs = useApi('/notifications', { query: { limit: 5 } });

  const personaKey = personaBucket
    ? {
        executive_sponsor: 'Executive Sponsor',
        delivery_leader: 'Portfolio Manager',
        data_steward: 'Data Steward',
      }[personaBucket] || 'default'
    : (user?.title || '').includes('Executive')
      ? 'Executive Sponsor'
      : (user?.title || '').includes('Portfolio')
        ? 'Portfolio Manager'
        : (user?.title || '').includes('Steward')
          ? 'Data Steward'
          : 'default';
  // Human wording for the page heading and quick-actions eyebrow — the actual
  // role name when we have one, never the raw 'default' token (IMP-002).
  const roleLabel = primaryRoleLabel(roles) || (personaKey !== 'default' ? personaKey : null);
  const summaryData = summary.data?.data || {};
  const counts = summaryData.counts || {};
  const hasOperationalData =
    Number(counts.portfolios || 0) +
      Number(counts.programs || 0) +
      Number(counts.projects || 0) +
      Number(counts.risks || 0) >
    0;
  // The empty-workspace quick set only applies when we actually inspected the
  // executive summary (exec/portfolio lens) and found no operational data.
  const quicks =
    !showPulse || hasOperationalData
      ? PERSONA_QUICKS[personaKey] || PERSONA_QUICKS.default
      : EMPTY_WORKSPACE_QUICKS[personaKey] || EMPTY_WORKSPACE_QUICKS.default;
  const homeKpis = summaryData.visuals?.kpiCards || [];
  const homeCard = (index, fallback) => homeKpis[index] || fallback;
  const homeBurndownCard = summaryData.visuals?.homeBurndownCard || null;

  // Already-fetched executive visuals that the old /home discarded. We render
  // exactly what the payload provides and omit (rather than fabricate) anything
  // missing. Field shapes confirmed against
  // apps/api/src/services/executiveSummary.service.js + analytics.routes.js.
  const visuals = summaryData.visuals || {};
  const gauges = visuals.gauges || {};
  const aggregateRoi = gauges.aggregateRoi || null;
  const missionOutcome = gauges.missionOutcome || null;
  const investmentSlices = (visuals.investmentDonut?.slices || []).filter(
    (s) => Number(s.value || 0) > 0,
  );
  const scatterPoints = visuals.missionImpactScatter?.points || [];
  const totalPlanned = investmentSlices.reduce((sum, s) => sum + Number(s.value || 0), 0);

  // projectsByHealth is a sibling of `visuals` on the payload, shaped
  // { green, amber, red, unknown }. Map health buckets to executive tones.
  const projectsByHealth = summaryData.projectsByHealth || {};
  const healthSegments = [
    { label: 'Healthy / on track', count: Number(projectsByHealth.green || 0), tone: 'sage' },
    { label: 'Watch', count: Number(projectsByHealth.amber || 0), tone: 'amber' },
    { label: 'At risk', count: Number(projectsByHealth.red || 0), tone: 'brick' },
    { label: 'Unscored', count: Number(projectsByHealth.unknown || 0), tone: 'slate' },
  ];
  const healthTotal = healthSegments.reduce((sum, s) => sum + s.count, 0);

  // Build the top KPI band from the same four signals the prior grid surfaced.
  // surface:'home' preserves the existing kpi-card-home-* test ids.
  const c0 = homeCard(0, {});
  const c1 = homeCard(1, {});
  const c2 = homeCard(2, {});
  const kpiItems = [
    {
      id: c0.id || 'home-summary-0',
      surface: 'home',
      label: c0.label || 'Portfolios',
      value: c0.value ?? summaryData.counts?.portfolios ?? (summary.loading ? '…' : '—'),
      // Render only payload-provided series; never fabricate a trend (AC-TRUST-2).
      sparkline: c0.series || null,
      sparklineTone: c0.sparklineTone || 'blue',
      footer:
        c0.richTooltip?.headlineValue?.secondary ||
        `Projects in flight: ${summaryData.counts?.projects ?? '—'}`,
      status: c0.status,
      statusLabel: c0.statusLabel,
      richTooltip: c0.richTooltip || null,
    },
    {
      id: homeBurndownCard?.id || 'home-sprint-burndown',
      surface: 'home',
      label: homeBurndownCard?.label || 'Sprint Burndown',
      value: homeBurndownCard?.value ?? (summary.loading ? '…' : '—'),
      unit: homeBurndownCard?.unit || 'pts',
      sparkline: homeBurndownCard?.series || null,
      sparklineTone: homeBurndownCard?.sparklineTone || 'amber',
      footer: homeBurndownCard?.footer || 'Active sprint delivery',
      status: homeBurndownCard?.status || 'amber',
      statusLabel: homeBurndownCard?.statusLabel || 'Watch',
      richTooltip: homeBurndownCard?.richTooltip || null,
    },
    {
      id: c1.id || 'home-summary-1',
      surface: 'home',
      label: c1.label || 'Active risks',
      value: c1.value ?? summaryData.counts?.risks ?? (summary.loading ? '…' : '—'),
      sparkline: c1.series || null,
      sparklineTone: c1.sparklineTone || 'amber',
      status: c1.status || 'amber',
      statusLabel: c1.statusLabel || 'Watch',
      richTooltip: c1.richTooltip || null,
    },
    {
      id: c2.id || 'home-summary-2',
      surface: 'home',
      label: c2.label || 'KPIs tracked',
      value: c2.value ?? summaryData.counts?.missionKpis ?? (summary.loading ? '…' : '—'),
      sparkline: c2.series || null,
      sparklineTone: c2.sparklineTone || 'sage',
      status: c2.status,
      statusLabel: c2.statusLabel,
      richTooltip: c2.richTooltip || null,
    },
  ];

  const hasGauges = Boolean(aggregateRoi || missionOutcome);
  const hasInvestment = investmentSlices.length > 0;
  const hasScatter = scatterPoints.length > 0;

  usePageMetadata({
    title: roleLabel ? `Mission Brief · ${roleLabel}` : 'Mission Brief',
    subtitle:
      showPulse && !hasOperationalData && !summary.loading && !summary.error
        ? 'This workspace is live with minimal launch data. Use the actions below to enter the clean-state flows.'
        : "Here's what needs you today: your reviews, decisions, and overdue items.",
  });

  return (
    <>
      {/* PRIME-SHELL-UX-001: single visible page identity, moved out of the
          topbar. Reads the route metadata (Mission Brief) with zero props and
          hosts the relocated "About this page" control beside the title. */}
      <PageHeader />
      {/* Compact onboarding rail (WP3): re-offers the guided journeys; hides
          itself once every tour is done or the user dismisses it. */}
      <StartHereStrip />

      <section className="home-attention-zone">
        <AttentionStrip attention={attention} quicks={quicks} />
      </section>

      {!showPulse ? null : summary.loading ? (
        <section className="home-pulse">
          <KpiBandSkeleton count={4} />
          <div className="bento" style={{ marginTop: 'var(--space-3)' }}>
            <div className="span-8 card">
              <ChartSkeleton height={280} />
            </div>
            <div className="span-4 card">
              <ChartSkeleton height={280} />
            </div>
          </div>
        </section>
      ) : summary.error ? (
        /* AC-STATE-1: the summary fetch failing banners its zone (mission pulse)
           while quick actions and notifications below keep rendering. */
        <section className="home-pulse">
          <ErrorZone
            title="mission pulse summary"
            error={summary.error}
            onRetry={summary.refresh}
          />
        </section>
      ) : (
        <section className="home-pulse">
          <div className="home-pulse-enter">
            <KpiHeroBand
              eyebrow={`Mission pulse · ${personaKey}`}
              asOf={summary.loadedAt}
              columns={4}
              items={kpiItems}
            />
          </div>

          {hasGauges || healthTotal > 0 || hasInvestment || hasScatter ? (
            <div className="bento" style={{ marginTop: 'var(--space-3)' }}>
              {hasGauges ? (
                <div className="span-4 card home-gauge-rail">
                  <header className="home-viz-head">
                    <div>
                      <div className="eyebrow">Executive outcomes</div>
                      <h2 className="card-title">ROI &amp; mission outcome</h2>
                    </div>
                  </header>
                  {aggregateRoi ? (
                    <GaugeCard
                      id={aggregateRoi.id || 'aggregate-roi'}
                      value={aggregateRoi.value}
                      max={4}
                      unit={aggregateRoi.unit || 'x'}
                      label={aggregateRoi.label || 'Aggregate ROI'}
                      sublabel={aggregateRoi.sublabel || 'Mission value / spend'}
                      tone={aggregateRoi.tone || 'sage'}
                      deltaLabel={aggregateRoi.deltaLabel}
                      deltaTone={aggregateRoi.deltaTone || 'sage'}
                      richTooltip={aggregateRoi.richTooltip || null}
                    />
                  ) : null}
                  {missionOutcome ? (
                    <GaugeCard
                      id={missionOutcome.id || 'mission-outcome'}
                      value={missionOutcome.value}
                      label={missionOutcome.label || 'Mission outcome score'}
                      sublabel={missionOutcome.sublabel}
                      tone={missionOutcome.tone || 'blue'}
                      deltaLabel={missionOutcome.deltaLabel}
                      deltaTone={missionOutcome.deltaTone || 'blue'}
                      richTooltip={missionOutcome.richTooltip || null}
                    />
                  ) : null}
                </div>
              ) : null}

              {healthTotal > 0 ? (
                <div className="span-4 card">
                  <header className="home-viz-head">
                    <div>
                      <div className="eyebrow">Delivery posture</div>
                      <h2 className="card-title">Projects by health</h2>
                    </div>
                  </header>
                  <div className="home-health-mix">
                    <StatusDonutMini
                      segments={healthSegments}
                      size={132}
                      totalLabel="Projects"
                      ariaLabel="Projects by health"
                    />
                  </div>
                </div>
              ) : null}

              {hasInvestment ? (
                <div className="span-4 card home-chart-cell">
                  <header className="home-viz-head">
                    <div>
                      <div className="eyebrow">Investment mix</div>
                      <h2 className="card-title">Planned spend by portfolio</h2>
                    </div>
                  </header>
                  <div className="home-chart-body">
                    <InvestmentDonut
                      data={investmentSlices}
                      centerValue={`$${totalPlanned.toFixed(0)}M`}
                      centerLabel="Planned"
                    />
                  </div>
                </div>
              ) : null}

              {hasScatter ? (
                <div className="span-8 card home-chart-cell">
                  <header className="home-viz-head">
                    <div>
                      <div className="eyebrow">Mission value</div>
                      <h2 className="card-title">Mission impact vs spend</h2>
                    </div>
                  </header>
                  <div className="home-chart-body">
                    <ScatterFitOrSummary points={scatterPoints} isMobile={isMobileShell} />
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="bento" style={{ marginTop: 'var(--space-3)' }}>
              <div className="span-12">
                <BrandedEmptyState
                  icon="📊"
                  eyebrow="Mission pulse"
                  title="No executive visuals yet"
                  description="This workspace is live with minimal launch data. Once portfolios, projects, and mission KPIs are populated, ROI, health, investment, and mission-impact visuals will appear here automatically."
                  tone="blue"
                />
              </div>
            </div>
          )}
        </section>
      )}

      <section className="bento">
        <div className="span-7 card">
          <header className="row spread" style={{ marginBottom: 'var(--space-3)' }}>
            <div>
              <div className="eyebrow">
                {roleLabel ? `Quick actions for ${roleLabel}` : 'Quick actions'}
              </div>
              <h2 className="card-title">Where to start</h2>
            </div>
          </header>
          <div className="bento">
            {quicks.map((q) => (
              <Link key={q.to} to={q.to} className="span-6 card interactive">
                <strong>{q.label}</strong>
                <div className="muted small">{q.desc}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="span-5 stack">
          {notifs.loading ? (
            <LoadingSkeleton rows={3} />
          ) : notifs.error ? (
            <ErrorZone title="notifications" error={notifs.error} onRetry={notifs.refresh} />
          ) : (notifs.data?.data || []).length === 0 ? (
            <EmptyState title="No recent notifications" />
          ) : (
            <>
              {(notifs.data?.data || []).slice(0, 3).map((n) => (
                <AlertCard
                  key={n.id}
                  severity={
                    /critical|high/i.test(n.severity || '')
                      ? 'red'
                      : /amber|warn/i.test(n.severity || '')
                        ? 'amber'
                        : 'blue'
                  }
                  title={n.title || n.body || n.id}
                  body={n.body || n.summary || ''}
                  meta={n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                  actionLabel="Open"
                  onAction={() => n.actionRoute && (window.location.href = n.actionRoute)}
                />
              ))}
              {(notifs.data?.page?.total || 0) > 3 ? (
                <Link className="btn ghost" to="/notifications">
                  View all notifications
                </Link>
              ) : null}
            </>
          )}
        </div>
      </section>
    </>
  );
}
